import { useMemo, useState } from 'react';
import type { CurrentRefinementsRenderState } from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';
import type { RangeRenderState } from 'instantsearch.js/es/connectors/range/connectRange';
import { useTranslation } from 'next-i18next';
import { useLocaleContext } from '../../../../context/localeContext';
import { Minus, Plus } from '../../../../icons';
import { getCurrencySymbol, localeToCurrencyCode } from '../../../../utils/currencyHelper';
import {
  ClearAll,
  Container,
  Header,
  HeaderText,
  Item,
  ItemList,
  ShowMoreButton,
  ShowMoreContainer,
} from './AppliedFilters.styles';
import { CurrentRefinement } from './CurrentRefinement';
import { getPriceRangeValue } from './utils/getPriceRangeValue';

export type AppliedFiltersProps = {
  clearAll?: () => void;
  items: CurrentRefinementsRenderState['items'];
  priceRange?: RangeRenderState;
};

export function AppliedFilters({ items, clearAll, priceRange }: AppliedFiltersProps) {
  const { t } = useTranslation(['lib-global-common']);
  const [showMore, setShowMore] = useState(false);
  const { locale } = useLocaleContext();
  const [statusMessage, setStatusMessage] = useState('');

  const { refinementListItems, canShowMore } = useMemo(() => {
    let currentRefinementList = items.flatMap((item) =>
      item.refinements.map((refinement) => ({
        ...refinement,
        itemRefine: item.refine,
      })),
    );

    if (priceRange && (priceRange.start[0] !== -Infinity || priceRange.start[1] !== Infinity)) {
      const { start, range, refine: priceRefine } = priceRange;
      const currencySymbol = getCurrencySymbol(localeToCurrencyCode[locale]);
      currentRefinementList = currentRefinementList.concat([
        {
          attribute: 'price.centAmount',
          value: getPriceRangeValue(start, range, currencySymbol),
          itemRefine: () => {
            priceRefine([range.min, range.max]);
          },
          type: 'numeric',
          label: 'price.centAmount',
        },
      ]);
    }
    const showMoreRefinements = currentRefinementList.length > 5;

    if (!showMore && showMoreRefinements) {
      return {
        refinementListItems: currentRefinementList.splice(0, 5),
        canShowMore: showMoreRefinements,
      };
    }
    return {
      refinementListItems: currentRefinementList,
      canShowMore: showMoreRefinements,
    };
  }, [items, locale, priceRange, showMore]);

  const handleShowMore = () => {
    setShowMore((prev) => !prev);
  };

  const updateStatusMessage = (msg: string) => {
    if (msg === statusMessage) {
      setStatusMessage(`${msg}\u200B`);
    } else {
      setStatusMessage(msg);
    }
  };

  const handleClearAll = () => {
    if (clearAll) {
      clearAll();
      updateStatusMessage(t('applied.filters.all.cleared'));
    }
  };

  return (
    <>
      <span role="status" aria-live="polite" className="visually-hidden">
        {statusMessage}
      </span>
      <Container data-testid="appliedFiltersSection" $isVisible={refinementListItems.length > 0}>
        {refinementListItems.length > 0 && (
          <>
            <Header>
              <HeaderText>{t('applied.filters.options')}</HeaderText>
              <ClearAll type="button" onClick={handleClearAll} data-testid="clearFiltersButton">
                {t('applied.filters.clear.all')}
              </ClearAll>
            </Header>
            <ItemList data-testid="appliedFiltersList">
              {refinementListItems.map((refinementItem) => (
                <Item key={`${refinementItem.attribute}-${refinementItem.label}`} data-testid="appliedFilterItem">
                  <CurrentRefinement refinementItem={refinementItem} updateStatusMessage={updateStatusMessage} />
                </Item>
              ))}
              {canShowMore && (
                <ShowMoreButton type="button" onClick={handleShowMore} data-testid="showMoreLessFiltersButton">
                  {showMore ? (
                    <ShowMoreContainer data-testid="showLessFilters">
                      <Minus aria-hidden /> {t('applied.filters.less')}
                    </ShowMoreContainer>
                  ) : (
                    <ShowMoreContainer data-testid="showMoreFilters">
                      <Plus aria-hidden /> {t('applied.filters.more')}
                    </ShowMoreContainer>
                  )}
                </ShowMoreButton>
              )}
            </ItemList>
          </>
        )}
      </Container>
    </>
  );
}

export default AppliedFilters;
